import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import { Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system';
import React, { MouseEventHandler, useState } from 'react';
import { toast } from 'react-toastify';

import { ContactInfoDto } from '@/api/generated';
import { DeleteDialog } from '@/components/ui/deleteDialog/DeleteDialog.comp.tsx';
import { themeColors } from '@/config/theme/themeColors.ts';
import { ToastConfig } from '@/config/ToastConfig.util.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { TextCutWithDotsStyles } from '@/styles/text.styles.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { AddCustomerContactInfoDialog } from '@/views/contactsDetails/components/header/components/addContactInfoButton/components/AddCustomerContactInfoDialog.comp.tsx';
import { useDeleteCustomerContactInfo } from '@/views/contactsDetails/components/header/components/addContactInfoButton/util/useDeleteContactInfo.util.ts';

interface Props {
  contactId: number;
  contactInfo: ContactInfoDto;
  hideButtons?: boolean;
}

export const ContactDetailsContactInfoItem: React.FC<Props> = props => {
  const { contactId, contactInfo, hideButtons } = props;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { deleteCustomerContactInfo } = useDeleteCustomerContactInfo();
  const { userId } = useUserContext();
  const { t } = useTranslationWithPrefix('views.contactsDetails.infos');

  const handleEditClick: MouseEventHandler<HTMLButtonElement> = event => {
    event.stopPropagation();
    event.preventDefault();
    setIsDialogOpen(true);
  };

  const handleCopyToClipboard = () => {
    void (async () => {
      await navigator.clipboard.writeText(contactInfo.value);
      toast(t('copiedSuccessfully'), ToastConfig.success);
    })();
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setIsDeleteDialogOpen(false);
  };

  const handleDeleteClick: MouseEventHandler<HTMLButtonElement> = event => {
    event.stopPropagation();
    event.preventDefault();
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    await deleteCustomerContactInfo({
      contactId,
      contactInfoId: contactInfo.contactInfoId,
      userId,
    });
  };

  if (hideButtons) {
    return (
      <Tooltip title={contactInfo.note}>
        <Box onClick={handleCopyToClipboard} sx={Styles}>
          <Stack spacing={0.5} sx={{ p: 1, px: 2 }}>
            <Typography variant="f14Medium">{contactInfo.value}</Typography>
            <Typography sx={TextCutWithDotsStyles} variant="f14Regular">
              {contactInfo.note ?? <div>&nbsp;</div>}
            </Typography>
          </Stack>
        </Box>
      </Tooltip>
    );
  }

  return (
    <>
      <Grid
        container
        onClick={handleCopyToClipboard}
        sx={[{ alignItems: 'center' }, ...(Array.isArray(Styles) ? Styles : [Styles])]}
      >
        <Grid size={10} sx={{ p: 2 }}>
          <Stack spacing={0.5}>
            <Typography variant="f14Medium">{contactInfo.value}</Typography>
            <Typography variant="f14Regular">{contactInfo.note}</Typography>
          </Stack>
        </Grid>
        <Grid sx={{ alignSelf: 'stretch', py: 2 }}>
          <Divider orientation="vertical" sx={{ m: 0 }} />
        </Grid>
        <Grid size={1.9}>
          <Stack spacing={0.5} sx={{ alignItems: 'center', height: '100%' }}>
            <IconButton onClick={handleEditClick} sx={{ height: 30, width: 30 }}>
              <EditOutlinedIcon />
            </IconButton>
            <IconButton onClick={handleDeleteClick} sx={{ height: 30, width: 30 }}>
              <DeleteForeverOutlinedIcon htmlColor={themeColors.red} />
            </IconButton>
          </Stack>
        </Grid>
      </Grid>
      {isDialogOpen && (
        <AddCustomerContactInfoDialog item={contactInfo} onClose={handleCloseDialog} />
      )}
      {isDeleteDialogOpen && (
        <DeleteDialog
          name={
            t('contactInfoTypeOptions.' + contactInfo.contactInfoType) + ': ' + contactInfo.value
          }
          onCancel={handleCloseDialog}
          onConfirm={handleDelete}
        />
      )}
    </>
  );
};

const Styles: SystemStyleObject<Theme> = {
  '&:hover': { backgroundColor: themeColors.stroke20, cursor: 'pointer' },
  border: `1px solid ${themeColors.stroke}`,
  borderLeft: `4px solid ${themeColors.accentColor}`,
  borderRadius: 2,
};
